import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  TextField,
  Checkbox,
  FormControlLabel,
  Alert,
} from "@mui/material";
import axios from "axios";

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL || process.env.REACT_APP_API_URL;

const pad2 = (n) => String(n).padStart(2, "0");

const fmtDate = (val) => {
  if (!val) return "—";
  const d = new Date(val);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
};

const fmtUsDateTimeStrict = (val) => {
  if (!val) return "—";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "—";
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const yyyy = d.getFullYear();
  let h = d.getHours();
  const AMPM = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  const hh = pad2(h);
  const min = pad2(d.getMinutes());
  return `${mm}/${dd}/${yyyy} ${hh}:${min} ${AMPM}`;
};

const isParsableDate = (v) => {
  if (!v || typeof v !== "string") return false;
  const d = new Date(v);
  return !isNaN(d.getTime());
};

// treat "", null, undefined, whitespace as "no content".
// numbers (including 0) and booleans are considered content.
const hasContent = (v) => {
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.trim().length > 0;
  if (Array.isArray(v)) return v.length > 0;
  if (v instanceof Date) return !isNaN(v.getTime());
  if (typeof v === "number") return true;
  if (typeof v === "boolean") return true;
  return true;
};

const normalizeYesNo = (v) => {
  if (v === null || v === undefined) return null;
  const t = String(v).trim().toLowerCase();
  if (!t) return null;
  if (["yes", "y", "true", "1"].includes(t)) return true;
  if (["no", "n", "false", "0"].includes(t)) return false;
  return null;
};

const DiagnosticAssessmentPreview = ({ patientformID, patientFormId, data }) => {
  const [form, setForm] = useState(null);
  const [header, setHeader] = useState(null);
  const [clientSigSrc, setClientSigSrc] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const resolvedPatientFormId = useMemo(
    () =>
      patientformID ??
      patientFormId ??
      data?.patientformID ??
      data?.patientFormId ??
      data?.id ??
      data?.Id ??
      null,
    [patientformID, patientFormId, data]
  );

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    const run = async () => {
      if (!resolvedPatientFormId) {
        setError("Missing patientFormId");
        setLoading(false);
        return;
      }
      if (!API_BASE_URL) {
        setError("API base URL is not set.");
        setLoading(false);
        return;
      }
      const token = localStorage.getItem("neuro_patientportal_auth_token");
      if (!token) {
        setError("Not authenticated (missing token)");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const headersCfg = { Authorization: `Bearer ${token}` };

        const [daRes, hdrRes] = await Promise.all([
          axios.get(
            `${API_BASE_URL}/Rehabilitative/getDiagnoticAssessmentById`,
            {
              params: { patientFormId: resolvedPatientFormId },
              headers: headersCfg,
              signal: controller.signal,
              validateStatus: (s) =>
                (s >= 200 && s < 300) || s === 204 || s === 404,
            }
          ),
          axios.get(`${API_BASE_URL}/Forms/GetPatientFormsbyId`, {
            params: { Id: resolvedPatientFormId },
            headers: headersCfg,
            signal: controller.signal,
            validateStatus: (s) =>
              (s >= 200 && s < 300) || s === 204 || s === 404,
          }),
        ]);

        if (!active) return;

        const da =
          daRes.status === 204 || daRes.status === 404 ? null : daRes.data;
        const hdr =
          hdrRes.status === 204 || hdrRes.status === 404 ? null : hdrRes.data;

        setForm(da);
        setHeader(hdr);

        const clientSigRaw =
          da?.clientsURL ||
          hdr?.clientsURL ||
          da?.clientSignatureURL ||
          da?.patientSignatureURL ||
          "";

        if (clientSigRaw) {
          if (/^(https?:|data:|blob:)/i.test(clientSigRaw)) {
            if (active) setClientSigSrc(clientSigRaw);
          } else {
            try {
              const res = await fetch(
                `${API_BASE_URL}/PatientDocument/ViewPatientDocument?path=${encodeURIComponent(
                  clientSigRaw
                )}`,
                { headers: headersCfg, signal: controller.signal }
              );
              if (res.ok) {
                const link = await res.text();
                if (active && link) setClientSigSrc(link);
              }
            } catch {
              /* ignore */
            }
          }
        } else {
          setClientSigSrc("");
        }

        if (!da && !hdr) {
          setError("No Diagnostic Assessment found for this patient.");
        }
      } catch (err) {
        if (!active) return;
        setError(
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load diagnostic assessment."
        );
        setForm(null);
        setHeader(null);
      } finally {
        if (active) setLoading(false);
      }
    };

    run();
    return () => {
      active = false;
      controller.abort();
    };
  }, [resolvedPatientFormId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={5}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Box p={4}>
        <Alert severity="warning">{error}</Alert>
      </Box>
    );
  }
  if (!form && !header) {
    return (
      <Box p={4}>
        <Typography>No data available.</Typography>
      </Box>
    );
  }

  // -------- HEADER VALUES / NORMALIZATION --------

  const rawAssessmentDate =
    form?.assessmentDate || header?.assessmentDate || header?.createdDate;
  const assessmentDate =
    rawAssessmentDate &&
      String(rawAssessmentDate).startsWith("0001-01-01")
      ? header?.createdDate || form?.createdDate || null
      : rawAssessmentDate;

  const clientName = header?.patientName || form?.patientName || "";
  const dob = header?.dob || form?.dob || header?.dOB || "";
  const fileId =
    header?.fileNo ||
    header?.fileno ||
    header?.accountNo ||
    form?.fileId ||
    "";
  const createdBy = form?.createdBy || header?.createdBy || "";

  const titleField =
    form?.customFormTitle ||
    header?.customFormTitle ||
    form?.title ||
    "Diagnostic Assessment";

  const clientSigDate =
    form?.patientSignatureDATE ||
    header?.patientSignatureDATE ||
    form?.clientSignatureDATE ||
    header?.clientSignatureDATE ||
    null;

  const buildSignedLine = (name, date) => {
    const nm = (name || "").trim();
    const dt = fmtUsDateTimeStrict(date);
    const hasDate = dt !== "—";
    if (nm && hasDate) return `Digitally signed by ${nm} on ${dt}.`;
    if (nm) return `Digitally signed by ${nm}.`;
    if (hasDate) return `Digitally signed on ${dt}.`;
    return "—";
  };

  const supName =
    form?.supervised ||
    header?.supervisedClinician ||
    "";
  const supDate =
    form?.supervisedDate ||
    header?.supervisedDate ||
    header?.supervisorSignatureDATE ||
    null;

  const internName = form?.internSignature || "";
  const internDate =
    form?.internDate || header?.internSignDate || header?.internDate || null;

  const providerName =
    form?.providerSignature ||
    header?.professionalName ||
    header?.providerName ||
    "";
  const providerDate =
    form?.providerDate || header?.providerDate || header?.professionalDate;

  const supervisorName =
    form?.supervisorSignature || header?.supervisorSignatureName || "";
  const supervisorDate =
    form?.supervisorDate || header?.supervisorDate || null;

  const startTimeDisplay = isParsableDate(form?.startTime)
    ? fmtUsDateTimeStrict(form?.startTime)
    : form?.startTime || "";
  const endTimeDisplay = isParsableDate(form?.endTime)
    ? fmtUsDateTimeStrict(form?.endTime)
    : form?.endTime || "";
  const interviewDateRaw =
    form?.assessmentInterviewDate || form?.interviewDate || null;
  const interviewDateDisplay = fmtDate(interviewDateRaw);

  const contributionsText =
    form?.contributionsAssessment || form?.contributional || "";

  // CAGE Questions
  const cageQuestionsConfig = [
    {
      key: "question1",
      label:
        "1. Have you ever felt that you ought to cut down on your drinking or drug use?",
    },
    {
      key: "question2",
      label:
        "2. Have people annoyed you by criticizing your drinking or drug use?",
    },
    {
      key: "question3",
      label:
        "3. Have you ever felt bad or guilty about your drinking or drug use?",
    },
    {
      key: "question4",
      label:
        "4. Have you ever had a drink or used drugs first thing in the morning to steady your nerves or to get rid of a hangover?",
    },
  ];

  const cageQuestions = cageQuestionsConfig.map((q) => ({
    ...q,
    value: normalizeYesNo(form?.[q.key]),
  }));
  const yesCount = cageQuestions.filter((q) => q.value === true).length;
  const screenedPositive = yesCount >= 1;

  // SDQ / CASII / ECSII / others
  const sdqList = form?.sdq || form?.sdqList || [];
  const assessmentTools =
    form?.assessmentsTools || form?.assessmentToolList || [];
  const assessmentMeasures =
    form?.assessmentMeasures || form?.assessmentMeasuresList || [];
  const dsM5List = form?.dsM5Diagnosis || form?.dsM5DiagnosisList || [];
  const supportiveAssessments =
    form?.supportiveAssessment || form?.supportiveAssessmentList || [];
  const contextualAssessments =
    form?.contextualAssessment || form?.contextualAssessmentList || [];
  const comprehensiveAssessments = form?.comprehensiveAssessment || [];

  // -------- SECTION ARRAYS / FLAGS --------

  const currentLifeFields = [
    ["Age", form?.age],
    ["Living Situation", form?.livingSituation],
    [
      "Basic Needs and Economic Status",
      form?.basicNeeds ?? form?.basicNeed ?? form?.basicneed ?? "",
    ],
    ["Education", form?.education],
    [
      "Significant Personal Relationships",
      form?.relationships ?? form?.significant ?? form?.significiant ?? "",
    ],
    ["Strengths and Resources", form?.strengths],
    ["Belief Systems", form?.beliefs ?? form?.beleifs ?? ""],
    [
      "Contextual Non-Personal Factors",
      form?.contextualFactors ?? form?.contextual ?? "",
    ],
    [
      "General Physical Health and Relationship to Client's Culture",
      form?.physicalHealth ?? form?.phisicalHealth ?? "",
    ],
    [
      "Current Medications",
      form?.currentMedications ?? form?.medication ?? "",
    ],
    ["Client Needs", form?.clientNeeds || ""],
  ].filter(([_, v]) => hasContent(v));

  const presentingProblemFields = [
    [
      "Perception of condition",
      form?.perceptionCondition ?? form?.preceptionCondition ?? "",
    ],
    ["Description of symptoms", form?.descriptionSymptoms || ""],
    ["Reason for referral", form?.referral ?? form?.referrel ?? ""],
    [
      "History of mental health and treatment",
      form?.history || "",
    ],
    [
      "Important Developmental Incidents",
      form?.importantIncident || "",
    ],
    ["Trauma history", form?.tramaHistory || ""],
    ["Maltreatment or abuse issues", form?.malTreatment || ""],
    [
      "History of alcohol and drug usage and treatment",
      form?.drugHistory || "",
    ],
    ["Health history", form?.healthHistory || ""],
    ["Family health history", form?.familyHealthHistory || ""],
    [
      "Cultural influences and their impact on the client",
      form?.cultural || "",
    ],
    ["Communication style", form?.communication || ""],
  ].filter(([_, v]) => hasContent(v));

  const mentalGeneralObsFields = [
    ["Appearance", form?.appearance],
    ["Build", form?.build],
    ["Demeanor / response to interviewer", form?.demeanor],
    ["Eye contact", form?.eyeContact],
    ["Activity level", form?.activityLevel],
    ["Speech", form?.speech],
  ].filter(([_, v]) => hasContent(v));

  const mentalThoughtContentFields = [
    ["Delusions", form?.delusions],
    ["Hallucinations", form?.hallucinations],
  ].filter(([_, v]) => hasContent(v));

  const mentalSelfDangerFields = [
    ["Self-Abuse", form?.selfAbuse],
    ["Violence to Others", form?.violence],
    ["Destruction to Property", form?.destruction],
  ].filter(([_, v]) => hasContent(v));

  const mentalCognitionFields = [
    ["Orientation", form?.orientation],
    ["Memory Issues", form?.memoryIssues],
    ["Attention", form?.attention],
    ["Judgment / Insight", form?.judgment],
  ].filter(([_, v]) => hasContent(v));

  const mentalThoughtProcessFields = [
    ["Mood", form?.mood],
    ["Affect", form?.affect],
    ["Behavior", form?.behavior],
    ["Anxiety", form?.anxiety],
  ].filter(([_, v]) => hasContent(v));

  const mentalExtraFields = [
    ["Intelligence Estimate", form?.intelligenceEstimate],
    [
      "Collaborative Notes (based on results above)",
      form?.collaborateNotes,
    ],
  ].filter(([_, v]) => hasContent(v));

  // CASII layout per spec
  const casiiSectionFields = [
    ["Risk of Harm", form?.riskHarm],
    ["Functional Status", form?.functionalStatusCASII],
    ["Co-Occurrence", form?.coOccurrence],
    ["Recovery-Emotional Stress", form?.recoveryStress],
    ["Recovery-Environmental Support", form?.recoverySupport],
    [
      "Resiliency and/or Responsiveness to Services",
      form?.resiliency,
    ],
    [
      "Child/Adolescent: Involvement in Services",
      form?.adolescent,
    ],
    [
      "Parent/Primary Caretaker: Involvement in Services",
      form?.parentCaretaker,
    ],
    ["Composite Score", form?.compositeScore],
    [
      "Level of Service Intensity Recommended",
      form?.serviceIntensity,
    ],
  ].filter(([_, v]) => hasContent(v));
  const hasCASIIData = casiiSectionFields.length > 0;

  // ECSII layout per spec
  const ecsiiStep1Fields = [
    ["Degree of Safety", form?.degreeSafety],
    ["Caregiving Relationships", form?.caregivingRelationships],
    ["Caregiving Environment/Strengths", form?.caregivingStrengths],
    ["Caregiving Environment/Stressors", form?.caregivingStressors],
    ["Functional/Developmental Status", form?.functionalStatusECSII],
    [
      "Impact of Medical/Develop/Emot-Bx Problems",
      form?.impactProblems,
    ],
    ["Total Score", form?.totalScoreESCII],
    ["Composite Score", form?.compositeScore],
  ].filter(([_, v]) => hasContent(v));

  const ecsiiStep2Field = hasContent(form?.sILevel)
    ? ["SI Level (initial)", form?.sILevel]
    : null;

  const ecsiiStep4Field = hasContent(form?.serviceIntensityLevel)
    ? ["Service Intensity Level (final)", form?.serviceIntensityLevel]
    : null;

  const ecsiiStep5Fields = [
    ["Involvement", form?.involvement],
    ["Fit", form?.fit],
    ["Effectiveness", form?.effectiveness],
  ].filter(([_, v]) => hasContent(v));

  const hasECSIIData =
    ecsiiStep1Fields.length > 0 ||
    ecsiiStep2Field != null ||
    ecsiiStep4Field != null ||
    ecsiiStep5Fields.length > 0;

  const hasSDQData = sdqList.length > 0;

  const hasAnyFunctionalData =
    hasSDQData || hasCASIIData || hasECSIIData;

  const dsmAxisFields = [
    ["Axis I Code", form?.axisICode],
    ["Axis I Description", form?.axisIDescription],
    ["Axis I Summary", form?.axisISummary],
    ["Axis II PIR Rating", form?.axisIIPIRRating],
    ["PIR Date", hasContent(form?.pirDate) ? fmtDate(form?.pirDate) : ""],
    ["PIR Summary", form?.pirSummary],
    ["Axis II Relationship", form?.axisIIRelationship],
    [
      "Relationship Date",
      hasContent(form?.relationshipDate)
        ? fmtDate(form?.relationshipDate)
        : "",
    ],
    ["Relationship Summary", form?.relationshipSummary],
    ["Axis III Disorders", form?.axisIIIDisorders],
    ["Axis IV Stressors", form?.axisIVStressors],
    ["Axis V Regulation", form?.axisVRegulation],
    ["Axis V Engagement", form?.axisVEngagement],
    ["Axis V Intentionality", form?.axisVIntentional],
    ["Axis V Gestures", form?.axisVGestures],
    ["Axis V Feelings", form?.axisVFeelings],
    ["Axis V Thinking", form?.axisVThinking],
  ].filter(([_, v]) => hasContent(v));

  const hasAnyScreeningData =
    hasContent(form?.screeningDate) ||
    hasContent(form?.screeningDetails) ||
    cageQuestions.some((q) => q.value !== null);

  const clientInfoHasData =
    hasContent(clientName) ||
    hasContent(dob) ||
    hasContent(fileId);

  const diagHasData =
    hasContent(startTimeDisplay) ||
    hasContent(endTimeDisplay) ||
    hasContent(interviewDateRaw) ||
    hasContent(contributionsText);

  const currentLifeHasData = currentLifeFields.length > 0;
  const presentingHasData = presentingProblemFields.length > 0;
  const mentalHasData =
    mentalGeneralObsFields.length > 0 ||
    mentalThoughtContentFields.length > 0 ||
    mentalSelfDangerFields.length > 0 ||
    mentalCognitionFields.length > 0 ||
    mentalThoughtProcessFields.length > 0 ||
    mentalExtraFields.length > 0;

  const clinicalSummaryHasData =
    hasContent(form?.clinicalSummary) ||
    hasContent(form?.furtherServices) ||
    hasContent(form?.diagnosticHypothesis);

  const assessmentAndToolsHasData =
    clinicalSummaryHasData ||
    dsmAxisFields.length > 0 ||
    dsM5List.length > 0 ||
    assessmentTools.length > 0 ||
    assessmentMeasures.length > 0 ||
    supportiveAssessments.length > 0 ||
    contextualAssessments.length > 0 ||
    comprehensiveAssessments.length > 0;

  return (
    <Paper sx={{ p: 0, borderRadius: 3, mb: 4 }}>
      {/* STICKY HEADER */}
      <Box
        sx={{
          position: "sticky",
          top: "-16px",
          zIndex: (t) => t.zIndex.appBar,
          bgcolor: "#fff",
          p: 4,
          pb: 0,
          mb: 3,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ position: "absolute", top: 16, left: 16 }}
        >
          <strong>Assessment Date:</strong> {fmtDate(assessmentDate)}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            position: "absolute",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            fontWeight: "bold",
          }}
        >
          Diagnostic Assessment
        </Typography>
        <Box sx={{ height: 28 }} />
        <Divider />
      </Box>

      {/* PATIENT STRIP */}
      <Box sx={{ px: 4 }}>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          {hasContent(fileId) && (
            <Grid item xs={12} md={4}>
              <Typography variant="body2">
                <strong>File #:</strong> {fileId}
              </Typography>
            </Grid>
          )}
          {hasContent(clientName) && (
            <Grid item xs={12} md={4} textAlign="center">
              <Typography variant="body2">
                <strong>Client:</strong> {clientName}
              </Typography>
            </Grid>
          )}
          {hasContent(createdBy) && (
            <Grid item xs={12} md={4} textAlign="right">
              <Typography variant="body2">
                <strong>Created By:</strong> {createdBy}
              </Typography>
            </Grid>
          )}
        </Grid>
        <Divider sx={{ mb: 3 }} />
      </Box>

      {/* TITLE + ASSESSMENT DATE FIELDS */}
      <Box sx={{ px: 4 }}>
        <Grid container spacing={2} sx={{ mb: 2, alignItems: "center" }}>
          {hasContent(titleField) && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Title"
                value={titleField}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          )}
          {hasContent(assessmentDate) && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Assessment Date"
                value={fmtDate(assessmentDate)}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          )}
        </Grid>
      </Box>

      <Divider sx={{ my: 2, mx: 4 }} />

      {/* MAIN CONTENT */}
      <Box sx={{ px: 4, pb: 4 }}>
        {/* CLIENT INFORMATION */}
        {clientInfoHasData && (
          <>
            <Typography variant="h6" gutterBottom>
              Client Information
            </Typography>
            <Grid container spacing={2}>
              {hasContent(clientName) && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Client Name"
                    value={clientName}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              )}
              {hasContent(dob) && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Date of Birth"
                    value={fmtDate(dob)}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              )}
              {hasContent(fileId) && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="File ID#"
                    value={fileId}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              )}
            </Grid>

            <Divider sx={{ my: 3 }} />
          </>
        )}

        {/* 1) DIAGNOSTIC ASSESSMENT */}
        {diagHasData && (
          <>
            <Typography variant="h6" gutterBottom>
              Diagnostic Assessment
            </Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              {hasContent(startTimeDisplay) && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Start Time"
                    value={startTimeDisplay}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              )}
              {hasContent(endTimeDisplay) && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="End Time"
                    value={endTimeDisplay}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              )}
              {hasContent(interviewDateRaw) && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Date of Assessment Interviews"
                    value={interviewDateDisplay}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              )}
            </Grid>

            {hasContent(contributionsText) && (
              <Box sx={{ mb: 3, mt: 1 }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  label="Contributions to the Assessment"
                  value={contributionsText}
                  InputProps={{ readOnly: true }}
                />
              </Box>
            )}

            <Divider sx={{ my: 3 }} />
          </>
        )}

        {/* 2) CURRENT LIFE SITUATION */}
        {currentLifeHasData && (
          <>
            <Typography variant="h6" gutterBottom>
              Current Life Situation
            </Typography>

            <Grid container spacing={2}>
              {currentLifeFields.map(([label, value], idx) => (
                <Grid item xs={12} sm={6} key={idx}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label={label}
                    value={value}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 3 }} />
          </>
        )}

        {/* 3) PRESENTING PROBLEMS AND REASON FOR REFERRALS */}
        {presentingHasData && (
          <>
            <Typography variant="h6" gutterBottom>
              Presenting Problems and Reason for Referrals
            </Typography>

            <Grid container spacing={2}>
              {presentingProblemFields.map(([label, value], idx) => (
                <Grid item xs={12} sm={6} key={idx}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label={label}
                    value={value}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 3 }} />
          </>
        )}

        {/* 4) MENTAL STATUS EXAM */}
        {mentalHasData && (
          <>
            <Typography variant="h6" gutterBottom>
              Mental Status Exam
            </Typography>

            {mentalGeneralObsFields.length > 0 && (
              <>
                <Typography variant="subtitle1" sx={{ mt: 1 }}>
                  General Observations
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {mentalGeneralObsFields.map(([label, value], idx) => (
                    <Grid item xs={12} sm={6} md={4} key={idx}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label={label}
                        value={value}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}

            {mentalThoughtContentFields.length > 0 && (
              <>
                <Typography variant="subtitle1" sx={{ mt: 2 }}>
                  Thought Content and Perception
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {mentalThoughtContentFields.map(([label, value], idx) => (
                    <Grid item xs={12} sm={6} key={idx}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label={label}
                        value={value}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}

            {mentalSelfDangerFields.length > 0 && (
              <>
                <Typography variant="subtitle1" sx={{ mt: 2 }}>
                  Self-Danger
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {mentalSelfDangerFields.map(([label, value], idx) => (
                    <Grid item xs={12} sm={4} key={idx}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label={label}
                        value={value}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}

            {mentalCognitionFields.length > 0 && (
              <>
                <Typography variant="subtitle1" sx={{ mt: 2 }}>
                  Cognition
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {mentalCognitionFields.map(([label, value], idx) => (
                    <Grid item xs={12} sm={6} md={3} key={idx}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label={label}
                        value={value}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}

            {(mentalThoughtProcessFields.length > 0 ||
              mentalExtraFields.length > 0) && (
                <>
                  <Typography variant="subtitle1" sx={{ mt: 2 }}>
                    Thought and Perceptual Process
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {mentalThoughtProcessFields.map(([label, value], idx) => (
                      <Grid item xs={12} sm={6} md={3} key={idx}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          label={label}
                          value={value}
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                    ))}
                    {mentalExtraFields.map(([label, value], idx) => (
                      <Grid item xs={12} sm={6} md={6} key={`extra-${idx}`}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          label={label}
                          value={value}
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}

            <Divider sx={{ my: 3 }} />
          </>
        )}

        {/* 5) SCREENINGS (CAGE) */}
        {hasAnyScreeningData && (
          <>
            <Typography variant="h6" gutterBottom>
              Screenings
            </Typography>

            {(hasContent(form?.screeningDate) ||
              hasContent(form?.screeningDetails)) && (
                <>
                  <Typography variant="subtitle1" sx={{ mt: 1 }}>
                    Screening Details
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {hasContent(form?.screeningDate) && (
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Date of Screening"
                          value={fmtDate(form?.screeningDate)}
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                    )}
                    {hasContent(form?.screeningDetails) && (
                      <Grid item xs={12} sm={8}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          label="Details"
                          value={form?.screeningDetails}
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                    )}
                  </Grid>
                </>
              )}

            {cageQuestions.some((q) => q.value !== null) && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>
                  Questions
                </Typography>

                {cageQuestions.map((q) =>
                  q.value === null ? null : (
                    <Grid
                      container
                      key={q.key}
                      alignItems="center"
                      sx={{ mb: 1.5 }}
                    >
                      <Grid item xs={12} md={8}>
                        <Typography variant="body2">{q.label}</Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <FormControlLabel
                            control={
                              <Checkbox checked={q.value === true} disabled />
                            }
                            label="Yes"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox checked={q.value === false} disabled />
                            }
                            label="No"
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  )
                )}
              </Box>
            )}

            <Typography variant="subtitle1" sx={{ mt: 1 }}>
              SCORE: Number of "Yes" Answers
            </Typography>
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Number of Yes answers"
                  value={yesCount}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox checked={screenedPositive} disabled />}
                  label="Screened positive = a score of 1 or greater"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />
          </>
        )}

        {/* 6) FUNCTIONAL ASSESSMENT (SDQ + CASII + ECSII) */}
        {hasAnyFunctionalData && (
          <>
            <Typography variant="h6" gutterBottom>
              Functional Assessment
            </Typography>

            <Typography variant="body2" sx={{ mb: 2 }}>
              A baseline functional assessment was completed as part of this
              assessment, utilizing the Strengths and Difficulties Questionnaire
              (SDQ), for ages 3–18, and either the Child and Adolescent Service
              Intensity Instrument (CASII) for ages 6–18 or the Early Childhood
              Service Intensity Instrument (ECSII) for ages 0–5. The results for
              these instruments are provided here.
            </Typography>

            {/* SDQ (ages 3–18) */}
            {hasSDQData && (
              <>
                <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>
                  SDQ (ages 3–18)
                </Typography>

                {sdqList.map((item) => (
                  <Box
                    key={item.id}
                    sx={{
                      mb: 2,
                      p: 2,
                      border: "1px solid #e0e0e0",
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Check the box for version completed:
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Checkbox checked={!!item.isSelf} disabled />
                        }
                        label="Self"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox checked={!!item.isParent} disabled />
                        }
                        label="Parent"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox checked={!!item.isTeacher} disabled />
                        }
                        label="Teacher"
                      />
                    </Box>

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      {[
                        ["Total Score", item.totalScore],
                        ["Emotional Problems", item.emotional],
                        ["Conduct Problems", item.conduct],
                        ["Inattention Problems", item.inattention],
                        ["Peer Problems", item.peer],
                        ["Pro-social Behavior", item.socialBehavior],
                        ["Impact Score", item.impactScore],
                      ]
                        .filter(([_, v]) => hasContent(v))
                        .map(([label, value], idx) => (
                          <Grid item xs={12} sm={6} md={4} key={idx}>
                            <TextField
                              fullWidth
                              label={label}
                              value={value}
                              InputProps={{ readOnly: true }}
                            />
                          </Grid>
                        ))}
                    </Grid>
                  </Box>
                ))}
              </>
            )}

            {/* CASII (ages 6–18) */}
            {hasCASIIData && (
              <>
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                  CASII (ages 6–18)
                </Typography>
                <FormControlLabel
                  control={<Checkbox checked disabled />}
                  label="Check to include this addendum in Assessment."
                  sx={{ mb: 1 }}
                />
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {casiiSectionFields.map(([label, value], idx) => (
                    <Grid item xs={12} sm={6} md={6} key={idx}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label={label}
                        value={value}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}

            {/* ECSII (0–5) */}
            {hasECSIIData && (
              <>
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                  ECSII (0–5)
                </Typography>
                <FormControlLabel
                  control={<Checkbox checked disabled />}
                  label="Check to include this addendum in Assessment."
                  sx={{ mb: 1 }}
                />

                {/* STEP 1 */}
                {ecsiiStep1Fields.length > 0 && (
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>
                      STEP 1:
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {ecsiiStep1Fields.map(([label, value], idx) => (
                        <Grid item xs={12} sm={6} md={6} key={idx}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            label={label}
                            value={value}
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}

                {/* STEP 2 */}
                {ecsiiStep2Field && (
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>
                      STEP 2:
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          label={ecsiiStep2Field[0]}
                          value={ecsiiStep2Field[1]}
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}

                {/* STEP 4 */}
                {ecsiiStep4Field && (
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>
                      STEP 4:
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          label={ecsiiStep4Field[0]}
                          value={ecsiiStep4Field[1]}
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}

                {/* STEP 5 */}
                {ecsiiStep5Fields.length > 0 && (
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>
                      STEP 5:
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {ecsiiStep5Fields.map(([label, value], idx) => (
                        <Grid item xs={12} sm={6} md={4} key={idx}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            label={label}
                            value={value}
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}
              </>
            )}

            <Divider sx={{ my: 3 }} />
          </>
        )}

        {/* 7) ASSESSMENT AND TOOLS (clinical summary, DSM, tools, etc.) */}
        {assessmentAndToolsHasData && (
          <>
            <Typography variant="h6" gutterBottom>
              Assessment and Tools
            </Typography>

            {/* Clinical Summary */}
            {clinicalSummaryHasData && (
              <>
                <Typography variant="subtitle1" sx={{ mt: 1 }}>
                  Clinical Summary
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {hasContent(form?.clinicalSummary) && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="Clinical Summary"
                        value={form?.clinicalSummary}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                  )}
                  {hasContent(form?.furtherServices) && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="Further Services / Recommendations"
                        value={form?.furtherServices}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                  )}
                  {hasContent(form?.diagnosticHypothesis) && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="Diagnostic Hypothesis"
                        value={form?.diagnosticHypothesis}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                  )}
                </Grid>
              </>
            )}

            {/* DSM / Axes */}
            {dsmAxisFields.length > 0 && (
              <>
                <Typography variant="subtitle1" sx={{ mt: 2 }}>
                  DSM-5 / Axes Summary
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {dsmAxisFields.map(([label, value], idx) => (
                    <Grid item xs={12} sm={6} key={idx}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label={label}
                        value={value}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}

            {/* DSM-5 Diagnoses */}
            {dsM5List.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  DSM-5 Diagnoses
                </Typography>
                {dsM5List.map((dx) => (
                  <Box
                    key={dx.id}
                    sx={{
                      mb: 2,
                      p: 2,
                      border: "1px solid #e0e0e0",
                      borderRadius: 2,
                    }}
                  >
                    {hasContent(dx.dSM5) && (
                      <Typography variant="subtitle2">
                        DSM-5: {dx.dSM5}
                        {dx.dSM5Detail ? ` (${dx.dSM5Detail})` : ""}
                      </Typography>
                    )}
                    {hasContent(dx.iCD10) && (
                      <Typography variant="body2">
                        ICD-10: {dx.iCD10}
                        {dx.iCD10Detail ? ` (${dx.iCD10Detail})` : ""}
                      </Typography>
                    )}
                    {hasContent(dx.specifier) && (
                      <Typography variant="body2">
                        Specifier: {dx.specifier}
                        {dx.specifierExplain
                          ? ` – ${dx.specifierExplain}`
                          : ""}
                      </Typography>
                    )}
                    {hasContent(dx.subtype) && (
                      <Typography variant="body2">
                        Subtype: {dx.subtype}
                        {dx.subtypeExplain
                          ? ` – ${dx.subtypeExplain}`
                          : ""}
                      </Typography>
                    )}
                    {hasContent(dx.severity) && (
                      <Typography variant="body2">
                        Severity: {dx.severity}
                        {dx.severityExplain
                          ? ` – ${dx.severityExplain}`
                          : ""}
                      </Typography>
                    )}
                    {hasContent(dx.otherFactors) && (
                      <Typography variant="body2">
                        Other Factors: {dx.otherFactors}
                      </Typography>
                    )}
                  </Box>
                ))}
              </>
            )}

            {/* Assessment Tools */}
            {assessmentTools.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Standardized Assessment Tools
                </Typography>
                {assessmentTools.map((tool) => (
                  <Box
                    key={tool.id}
                    sx={{
                      mb: 2,
                      p: 2,
                      border: "1px solid #e0e0e0",
                      borderRadius: 2,
                    }}
                  >
                    {hasContent(tool.instrument) && (
                      <Typography variant="subtitle2">
                        Instrument: {tool.instrument}
                      </Typography>
                    )}
                    {hasContent(tool.score) && (
                      <Typography variant="body2">
                        Score: {tool.score}
                      </Typography>
                    )}
                    {hasContent(tool.datasource) && (
                      <Typography variant="body2">
                        Data Source: {tool.datasource}
                      </Typography>
                    )}
                    {hasContent(tool.phase) && (
                      <Typography variant="body2">
                        Phase: {tool.phase}
                      </Typography>
                    )}
                    {hasContent(tool.testedDate) && (
                      <Typography variant="body2">
                        Tested Date: {fmtDate(tool.testedDate)}
                      </Typography>
                    )}
                    {hasContent(tool.detail) && (
                      <Typography variant="body2">
                        Detail: {tool.detail}
                      </Typography>
                    )}
                  </Box>
                ))}
              </>
            )}

            {/* Assessment Measures */}
            {assessmentMeasures.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Assessment Measures
                </Typography>
                {assessmentMeasures.map((m) => (
                  <Box
                    key={m.id}
                    sx={{
                      mb: 2,
                      p: 2,
                      border: "1px solid #e0e0e0",
                      borderRadius: 2,
                    }}
                  >
                    {hasContent(m.assessmenttool) && (
                      <Typography variant="subtitle2">
                        Assessment Tool: {m.assessmenttool}
                      </Typography>
                    )}
                    {hasContent(m.other) && (
                      <Typography variant="body2">
                        Other: {m.other}
                      </Typography>
                    )}
                    {hasContent(m.detail) && (
                      <Typography variant="body2">
                        Detail: {m.detail}
                      </Typography>
                    )}
                  </Box>
                ))}
              </>
            )}

            {/* Supportive Assessments */}
            {supportiveAssessments.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Supportive Assessments
                </Typography>
                {supportiveAssessments.map((s) => (
                  <Box
                    key={s.id}
                    sx={{
                      mb: 2,
                      p: 2,
                      border: "1px solid #e0e0e0",
                      borderRadius: 2,
                    }}
                  >
                    {(hasContent(s.name) || hasContent(s.date)) && (
                      <Typography variant="subtitle2">
                        {s.name || "Unnamed"}
                        {hasContent(s.date) ? ` – ${fmtDate(s.date)}` : ""}
                      </Typography>
                    )}
                    {hasContent(s.description) && (
                      <Typography variant="body2">
                        Description: {s.description}
                      </Typography>
                    )}
                    {hasContent(s.notes) && (
                      <Typography variant="body2">
                        Notes: {s.notes}
                      </Typography>
                    )}
                  </Box>
                ))}
              </>
            )}

            {/* Contextual Assessments */}
            {contextualAssessments.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Contextual Assessments
                </Typography>
                {contextualAssessments.map((c) => (
                  <Box
                    key={c.id}
                    sx={{
                      mb: 2,
                      p: 2,
                      border: "1px solid #e0e0e0",
                      borderRadius: 2,
                    }}
                  >
                    {hasContent(c.assessmentType) && (
                      <Typography variant="subtitle2">
                        Assessment Type: {c.assessmentType}
                      </Typography>
                    )}
                    {hasContent(c.triggers) && (
                      <Typography variant="body2">
                        External Triggers: {c.triggers}
                      </Typography>
                    )}
                    {hasContent(c.responses) && (
                      <Typography variant="body2">
                        External Responses: {c.responses}
                      </Typography>
                    )}
                    {hasContent(c.consequencesImmediate) && (
                      <Typography variant="body2">
                        External Consequences (Immediate):{" "}
                        {c.consequencesImmediate}
                      </Typography>
                    )}
                    {hasContent(c.consequencesDellayed) && (
                      <Typography variant="body2">
                        External Consequences (Delayed):{" "}
                        {c.consequencesDellayed}
                      </Typography>
                    )}
                    {hasContent(c.internaltriggers) && (
                      <Typography variant="body2">
                        Internal Triggers: {c.internaltriggers}
                      </Typography>
                    )}
                    {hasContent(c.internalresponses) && (
                      <Typography variant="body2">
                        Internal Responses: {c.internalresponses}
                      </Typography>
                    )}
                    {hasContent(c.internalconsequencesImmediate) && (
                      <Typography variant="body2">
                        Internal Consequences (Immediate):{" "}
                        {c.internalconsequencesImmediate}
                      </Typography>
                    )}
                    {hasContent(c.internalconsequencesDellayed) && (
                      <Typography variant="body2">
                        Internal Consequences (Delayed):{" "}
                        {c.internalconsequencesDellayed}
                      </Typography>
                    )}
                  </Box>
                ))}
              </>
            )}

            {/* Comprehensive Assessments */}
            {comprehensiveAssessments.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Comprehensive Assessments
                </Typography>
                {comprehensiveAssessments.map((c) => (
                  <Box
                    key={c.id}
                    sx={{
                      mb: 2,
                      p: 2,
                      border: "1px solid #e0e0e0",
                      borderRadius: 2,
                    }}
                  >
                    {hasContent(c.functionalStatus) && (
                      <Typography variant="subtitle2">
                        Functional Status: {c.functionalStatus}
                      </Typography>
                    )}
                    {hasContent(c.timeFrame) && (
                      <Typography variant="body2">
                        Time Frame: {c.timeFrame}
                      </Typography>
                    )}
                    {hasContent(c.mentalHealth) && (
                      <Typography variant="body2">
                        Mental Health: {c.mentalHealth}
                      </Typography>
                    )}
                    {hasContent(c.substance) && (
                      <Typography variant="body2">
                        Substance: {c.substance}
                      </Typography>
                    )}
                    {hasContent(c.interactions) && (
                      <Typography variant="body2">
                        Interactions: {c.interactions}
                      </Typography>
                    )}
                  </Box>
                ))}
              </>
            )}

            <Divider sx={{ my: 4 }} />
          </>
        )}

        {/* SIGNATURES (no client signature) */}
        <Typography variant="h6" gutterBottom>
          Signatures
        </Typography>

        {/* Supervised Clinician */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">
            <strong>Supervised Clinician:</strong>
          </Typography>
          <Typography sx={{ fontWeight: 600, mt: 0.5 }}>
            {buildSignedLine(supName, supDate)}
          </Typography>
        </Box>

        {/* Intern */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">
            <strong>Intern Signature:</strong>
          </Typography>
          <Typography sx={{ fontWeight: 600, mt: 0.5 }}>
            {buildSignedLine(internName, internDate)}
          </Typography>
        </Box>

        {/* Provider */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">
            <strong>Provider Signature:</strong>
          </Typography>
          <Typography sx={{ fontWeight: 600, mt: 0.5 }}>
            {buildSignedLine(providerName, providerDate)}
          </Typography>
        </Box>

        {/* Supervisor */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle2">
            <strong>Supervisor Signature:</strong>
          </Typography>
          <Typography sx={{ fontWeight: 600, mt: 0.5 }}>
            {buildSignedLine(supervisorName, supervisorDate)}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default DiagnosticAssessmentPreview;